<template>
    <svg v-bind="svgAttributes" v-html="svgContent"></svg>
</template>

<script setup lang="ts">
import { computed } from "vue";

defineOptions({ name: "SvgVue" });

const props = defineProps<{
    icon?: string;
}>();

/**
 * Inline SVG registry replacing the laravel-mix-svg-vue / svg-vue3 pair. Every
 * file under resources/svg is eagerly imported as a raw string and keyed by its
 * path so an icon name resolves to its markup at runtime. This preserves the
 * dynamic `<svg-vue :icon="name" />` lookup (e.g. `payments/visa`,
 * `flags/us`) that loader-based approaches cannot offer.
 */
const icons = import.meta.glob<string>("/resources/svg/**/*.svg", {
    query: "?raw",
    import: "default",
    eager: true,
});

/**
 * Parse the raw SVG once and split it into the root <svg> element's
 * attributes and its inner markup. Parsing via the DOM (rather than a
 * regex) tolerates files that carry an XML prologue or comments before
 * the <svg> tag.
 */
const parsed = computed<{
    attributes: Record<string, string>;
    content: string;
}>(() => {
    const raw =
        props.icon === undefined
            ? undefined
            : icons[`/resources/svg/${props.icon.replace(/\./g, "/")}.svg`];

    if (!raw) {
        return { attributes: {}, content: "" };
    }

    const wrapper = document.createElement("div");
    wrapper.innerHTML = raw;

    const svg = wrapper.querySelector("svg");

    if (!svg) {
        return { attributes: {}, content: "" };
    }

    const attributes: Record<string, string> = {};
    Array.from(svg.attributes).forEach((attribute) => {
        attributes[attribute.name] = attribute.value;
    });

    return { attributes, content: svg.innerHTML };
});

const svgAttributes = computed(() => parsed.value.attributes);
const svgContent = computed(() => parsed.value.content);
</script>
